import React, { useState, useEffect, Suspense, lazy } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useUser } from '@/providers/UserProvider'
import { isManager } from '@/utils/role'

// Lazy loading des composants d'onglets
const DispatchTab = lazy(() => import('@/components/DispatchTab'))
const StockTab = lazy(() => import('@/components/StockTab'))
const AccountingTab = lazy(() => import('@/components/AccountingTab'))
const HumanResourcesTab = lazy(() => import('@/components/HumanResourcesTab'))
const CarStorageTab = lazy(() => import('@/components/CarStorageTab'))
const ContactsTab = lazy(() => import('@/components/ContactsTab'))
const EventsTab = lazy(() => import('@/components/EventsTab'))
const PublicServicesTab = lazy(() => import('@/components/PublicServicesTab'))
const GiftCardsTab = lazy(() => import('@/components/GiftCardsTab'))

import { Navigation } from '@/components/Navigation'
import LoadingSpinner from '@/components/UI/LoadingSpinner'
import { ROUTES } from '@/utils/constants'
import '@/styles/index.css'
import '@/styles/loading.css'

type TabType = 'dispatch' | 'stock' | 'accounting' | 'hr' | 'carStorage' | 'contacts' | 'events' | 'publicServices' | 'giftCards'

// Composant interne pour gérer la logique de navigation
const AppContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading } = useUser()

  // Get current tab from URL path
  const getCurrentTabFromPath = (): TabType => {
    const path = location.pathname
    const startsWith = (prefix: string): boolean => path === prefix || path.startsWith(`${prefix}/`)
    if (startsWith('/dispatch')) return 'dispatch'
    if (startsWith('/stock')) return 'stock'
    if (startsWith('/accounting')) return 'accounting'
    if (startsWith('/hr')) return 'hr'
    if (startsWith('/car-storage')) return 'carStorage'
    if (startsWith('/contacts')) return 'contacts'
    if (startsWith('/events')) return 'events'
    if (startsWith('/public-services')) return 'publicServices'
    if (startsWith('/gift-cards')) return 'giftCards'
    return 'dispatch' // default
  }

  const [currentTab, setCurrentTab] = useState<TabType>(getCurrentTabFromPath())

  // Update tab when URL changes
  useEffect(() => {
    const tabFromPath = getCurrentTabFromPath()
    if (tabFromPath !== currentTab) {
      setCurrentTab(tabFromPath)
    }
  }, [location.pathname, currentTab])

  const tabPaths: Record<TabType, string> = {
    dispatch: ROUTES.DISPATCH,
    stock: ROUTES.STOCK,
    accounting: ROUTES.ACCOUNTING,
    hr: ROUTES.HR,
    carStorage: ROUTES.CAR_STORAGE,
    contacts: ROUTES.CONTACTS,
    events: ROUTES.EVENTS,
    publicServices: ROUTES.PUBLIC_SERVICES,
    giftCards: ROUTES.GIFT_CARDS,
  }

  const handleTabChange = (tab: TabType) => {
    setCurrentTab(tab)
    navigate(tabPaths[tab])
  }

  // Guard : attendre la résolution auth avant de rendre quoi que ce soit
  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '100vh' }}>
        <LoadingSpinner size="lg" message="Chargement..." />
      </div>
    )
  }

  // Non authentifié → page de login
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Modules sensibles réservés aux managers
  const restrictedPaths = ['/accounting', '/hr', '/events', '/stock/logs', '/stock/suppliers']
  if (
    !isManager(user.role) &&
    restrictedPaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
  ) {
    return <Navigate to="/dispatch" replace />
  }

  return (
    <>
      <Navigation currentTab={currentTab} onTabChange={handleTabChange} />

      {/* Main Content */}
      <main className="main-content">
        <Suspense fallback={
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" message="Chargement de l'onglet..." />
          </div>
        }>
          <Routes>
            <Route path="/" element={<DispatchTab />} />
            <Route path="/dispatch" element={<DispatchTab />} />
            <Route path="/dispatch/*" element={<DispatchTab />} />
            <Route path="/stock" element={<StockTab />} />
            <Route path="/stock/:subtab" element={<StockTab />} />
            <Route path="/accounting" element={<AccountingTab />} />
            <Route path="/accounting/:subtab" element={<AccountingTab />} />
            <Route path="/hr" element={<HumanResourcesTab />} />
            <Route path="/hr/*" element={<HumanResourcesTab />} />
            <Route path="/car-storage" element={<CarStorageTab />} />
            <Route path="/car-storage/:subtab" element={<CarStorageTab />} />
            <Route path="/contacts" element={<ContactsTab />} />
            <Route path="/contacts/:subtab" element={<ContactsTab />} />
            <Route path="/events" element={<EventsTab />} />
            <Route path="/events/:id" element={<EventsTab />} />
            <Route path="/public-services" element={<PublicServicesTab />} />
            <Route path="/gift-cards" element={<GiftCardsTab />} />
            <Route path="/gift-cards/:subtab" element={<GiftCardsTab />} />
          </Routes>
        </Suspense>
      </main>
    </>
  )
}

function App() {
  return <AppContent />
}

export default App
